import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Play, Pause, RotateCcw } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }
interface Nucleus { x: number; y: number; th: number } // th = ngưỡng phân rã (ngẫu nhiên cố định)

function sphere(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, hi: string, base: string, edge: string) {
  const g = ctx.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.12, x, y, r)
  g.addColorStop(0, hi); g.addColorStop(0.45, base); g.addColorStop(1, edge)
  ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}

// Phóng xạ (Lớp 12 — Bài 21): N(t) = N0·2^(−t/T).
// Kéo thanh trượt t → điểm chạy trên đồ thị, số hạt nhân còn lại giảm theo đúng quy luật.
const RadioactiveDecaySimulation: React.FC<Props> = ({
  title = 'Mô phỏng Phóng xạ',
  description = 'Kéo thanh trượt thời gian t để quan sát số hạt nhân còn lại N = N₀·2^(−t/T) giảm dần và điểm chạy trên đồ thị.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const lastRef = useRef<number | null>(null)
  const nucleiRef = useRef<Nucleus[]>([])
  const [halfLife, setHalfLife] = useState(5)
  const [n0, setN0] = useState(200)
  const [t, setT] = useState(0)
  const [running, setRunning] = useState(false)
  const firstRef = useRef(false)
  const W = 720, H = 300

  const Tmax = halfLife * 6

  const build = useCallback((count: number) => {
    const cols = Math.ceil(Math.sqrt(count))
    nucleiRef.current = Array.from({ length: count }, (_, i) => ({
      x: i % cols, y: Math.floor(i / cols), th: Math.random(),
    }))
  }, [])
  useEffect(() => { build(n0); setT(0); setRunning(false) }, [n0, build])

  const remaining = Math.round(n0 * Math.pow(2, -t / halfLife))

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    const f = 1 - Math.pow(2, -t / halfLife) // tỉ lệ đã phân rã
    const nuclei = nucleiRef.current
    const cols = Math.ceil(Math.sqrt(nuclei.length))
    const gridW = Math.floor(W * 0.48)
    const cell = Math.min((gridW - 20) / cols, (H - 20) / cols)
    const r = Math.max(2.5, cell * 0.34)
    for (const nu of nuclei) {
      const cx = 12 + nu.x * cell + cell / 2
      const cy = 12 + nu.y * cell + cell / 2
      if (nu.th < f) { ctx.fillStyle = 'rgba(148,163,184,0.28)'; ctx.beginPath(); ctx.arc(cx, cy, r * 0.8, 0, Math.PI * 2); ctx.fill() }
      else sphere(ctx, cx, cy, r, '#bbf7d0', '#10b981', '#065f46')
    }

    // ── Đồ thị N(t) ──
    const gx0 = gridW + 30, gy0 = H - 30, gw = W - gx0 - 18, gh = H - 52
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1.5
    ctx.beginPath(); ctx.moveTo(gx0, 14); ctx.lineTo(gx0, gy0); ctx.lineTo(gx0 + gw, gy0); ctx.stroke()
    ctx.fillStyle = '#475569'; ctx.font = 'bold 12px sans-serif'
    ctx.fillText('N', gx0 - 6, 12); ctx.fillText('t (s)', gx0 + gw - 26, gy0 + 18)
    // mốc chu kì bán rã
    ctx.strokeStyle = 'rgba(148,163,184,0.45)'; ctx.setLineDash([4, 3]); ctx.lineWidth = 1
    for (let k = 1; k <= 6; k++) { const px = gx0 + (k * halfLife / Tmax) * gw; ctx.beginPath(); ctx.moveTo(px, 14); ctx.lineTo(px, gy0); ctx.stroke() }
    ctx.setLineDash([])
    // đường lý thuyết (ĐẬM, rõ)
    // CHỈ vẽ phần đã chạy qua (phía SAU điểm) — không hiện toàn bộ đường cong phía trước
    ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 3.5; ctx.beginPath()
    const tCur = Math.min(t, Tmax)
    const iMax = Math.ceil((tCur / Tmax) * 120)
    for (let i = 0; i <= iMax; i++) {
      const tt = Math.min(tCur, (i / 120) * Tmax)
      const n = n0 * Math.pow(2, -tt / halfLife)
      const px = gx0 + (tt / Tmax) * gw, py = gy0 - (n / n0) * gh
      i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)
    }
    ctx.stroke()
    // điểm CHẠY hiện tại
    const px = gx0 + Math.min(t / Tmax, 1) * gw
    const py = gy0 - (remaining / n0) * gh
    ctx.strokeStyle = 'rgba(239,68,68,0.5)'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 3])
    ctx.beginPath(); ctx.moveTo(px, gy0); ctx.lineTo(px, py); ctx.moveTo(gx0, py); ctx.lineTo(px, py); ctx.stroke(); ctx.setLineDash([])
    ctx.fillStyle = '#ef4444'; ctx.beginPath(); ctx.arc(px, py, 6, 0, Math.PI * 2); ctx.fill()
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke()
  }, [t, halfLife, n0, remaining, Tmax])

  useEffect(() => { draw() }, [draw])

  // Play: tự tăng t
  const tick = useCallback((ts: number) => {
    if (lastRef.current == null) lastRef.current = ts
    const dt = Math.min(0.05, (ts - lastRef.current) / 1000); lastRef.current = ts
    setT((prev) => {
      const nt = prev + dt
      if (nt >= Tmax) { setRunning(false); if (!firstRef.current) { firstRef.current = true; onComplete?.() } return Tmax }
      return nt
    })
    raf.current = requestAnimationFrame(tick)
  }, [Tmax, onComplete])

  useEffect(() => {
    if (running) { lastRef.current = null; raf.current = requestAnimationFrame(tick) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, tick])

  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-4 my-3">
      <h4 className="font-bold text-emerald-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100 bg-slate-50" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3 text-center">
        <div className="p-2 rounded-lg bg-emerald-50"><div className="text-lg font-black text-emerald-700">{remaining}</div><div className="text-[11px] text-gray-500">Còn lại N</div></div>
        <div className="p-2 rounded-lg bg-slate-100"><div className="text-lg font-black text-slate-600">{n0 - remaining}</div><div className="text-[11px] text-gray-500">Đã phân rã</div></div>
        <div className="p-2 rounded-lg bg-blue-50"><div className="text-lg font-black text-blue-700">{t.toFixed(1)}s</div><div className="text-[11px] text-gray-500">Thời gian t</div></div>
        <div className="p-2 rounded-lg bg-amber-50"><div className="text-lg font-black text-amber-700">{halfLife}s</div><div className="text-[11px] text-gray-500">Chu kì bán rã T</div></div>
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600 font-semibold">⏱ Thời gian t</span>
          <input type="range" min={0} max={Tmax} step={0.1} value={t} onChange={(e) => { setRunning(false); setT(Number(e.target.value)) }} className="flex-1 accent-red-500" />
          <span className="w-14 text-right font-semibold">{t.toFixed(1)}s</span></label>
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Chu kì bán rã T</span>
          <input type="range" min={1} max={15} step={1} value={halfLife} disabled={running} onChange={(e) => { setHalfLife(Number(e.target.value)); setT(0) }} className="flex-1" />
          <span className="w-14 text-right font-semibold">{halfLife}s</span></label>
        <label className="flex items-center gap-2 text-sm"><span className="w-28 text-gray-600">Số hạt ban đầu N₀</span>
          <input type="range" min={50} max={400} step={50} value={n0} disabled={running} onChange={(e) => setN0(Number(e.target.value))} className="flex-1" />
          <span className="w-14 text-right font-semibold">{n0}</span></label>
      </div>

      <div className="flex items-center gap-2 mt-3">
        <button onClick={() => { if (t >= Tmax) setT(0); setRunning((r) => !r) }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700">
          {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy tự động</>}
        </button>
        <button onClick={() => { setRunning(false); setT(0) }}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 font-bold hover:bg-gray-50">
          <RotateCcw className="w-4 h-4" /> Đặt lại
        </button>
      </div>
    </div>
  )
}

export default RadioactiveDecaySimulation
